import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { faPen, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PetCustomPrice, PetCustomPriceUpdatePayload } from '@spike/pet-model';
import {
    PetsStatus,
    deletePetCustomPriceThunk,
    fetchPetCustomPricesThunk,
    updatePetCustomPriceThunk
} from '@spike/pets-action';
import useNonInitialEffect from '@versiondos/hooks';
import { ConfirmDialog } from 'components/UI';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import Pagination from 'components/UI/Pagination/Pagination';
import { Spinner } from 'components/UI/V3/Spinner/Spinner';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import useIsMobile from 'hooks/useIsMobile';
import moment from 'moment-timezone';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    CustomPriceDisplayRow,
    formatCustomPrice,
    getTransportationPillLabel,
    toCustomPriceDisplayRows
} from './customPriceRows';
import EditCustomPriceModal from './EditCustomPriceModal';
import styles from './PetCustomPricesTab.module.scss';

interface Props {
    petId: number;
}

export const PetCustomPricesTab: FunctionComponent<Props> = ({ petId }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timezone = useTimeZone();
    const isMobile = useIsMobile();

    const customPrices = useSelector<RootState, Array<PetCustomPrice>>(state => state.pets.customPrices);
    const customPricesLoading = useSelector<RootState, boolean>(state => state.pets.customPricesLoading);
    const petsStatus = useSelector<RootState, PetsStatus>(state => state.pets.status);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loadedPetId, setLoadedPetId] = useState<number | null>(null);
    const [rowToDelete, setRowToDelete] = useState<CustomPriceDisplayRow | null>(null);
    const [rowToEdit, setRowToEdit] = useState<CustomPriceDisplayRow | null>(null);

    useEffect(() => {
        setPage(1);
        setLoadedPetId(null);
        setRowToDelete(null);
        setRowToEdit(null);
        dispatch(fetchPetCustomPricesThunk(apiClientWrapper, petId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId]);

    useEffect(() => {
        if (!customPricesLoading) {
            setLoadedPetId(petId);
        }
    }, [customPricesLoading, petId]);

    useNonInitialEffect(() => {
        if (
            (petsStatus === PetsStatus.DeleteCustomPriceSuccess ||
                petsStatus === PetsStatus.UpdateCustomPriceSuccess) &&
            rowToDelete
        ) {
            setRowToDelete(null);
        }
    }, [petsStatus]);

    const rows = useMemo(() => toCustomPriceDisplayRows(customPrices || []), [customPrices]);

    const totalCount = rows.length;
    const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;
    const safePage = Math.min(page, totalPages);
    const pageRows = useMemo(() => {
        if (pageSize === 0) return rows;
        const start = (safePage - 1) * pageSize;
        return rows.slice(start, start + pageSize);
    }, [rows, pageSize, safePage]);

    const isLoading = customPricesLoading || loadedPetId !== petId;
    const isDeleting = petsStatus === PetsStatus.DeletingCustomPrice;
    const isUpdating = petsStatus === PetsStatus.UpdatingCustomPrice;

    const formatLastUpdated = (updatedAt: string) => {
        const date = moment(updatedAt).tz(timezone);
        if (!date.isValid()) return '—';
        return date.format('MMM D, YYYY [at] h:mma');
    };

    const handleConfirmDelete = () => {
        if (!rowToDelete) return;

        if (rowToDelete.hasSiblingOnSameRecord) {
            const payload: PetCustomPriceUpdatePayload =
                rowToDelete.priceType === 'pickup' ? { pickupPrice: null } : { dropoffPrice: null };
            dispatch(updatePetCustomPriceThunk(apiClientWrapper, petId, rowToDelete.customPriceId, payload));
            return;
        }

        dispatch(deletePetCustomPriceThunk(apiClientWrapper, petId, rowToDelete.customPriceId));
    };

    const handleSaveEdit = (payload: PetCustomPriceUpdatePayload) => {
        if (!rowToEdit) return;
        dispatch(updatePetCustomPriceThunk(apiClientWrapper, petId, rowToEdit.customPriceId, payload));
    };

    const renderServiceCell = (row: CustomPriceDisplayRow) => {
        const pillLabel = getTransportationPillLabel(row.priceType);
        return (
            <Box className={styles.serviceCell}>
                <Typography className={styles.serviceName}>{row.serviceName}</Typography>
                {pillLabel && <span className={styles.transportPill}>{pillLabel}</span>}
            </Box>
        );
    };

    const renderActions = (row: CustomPriceDisplayRow) => (
        <Box className={styles.actions}>
            <button
                type="button"
                aria-label="Delete custom price"
                className={styles.actionButton}
                onClick={() => setRowToDelete(row)}
            >
                <FontAwesomeIcon icon={faTrashAlt} />
            </button>
            <button
                type="button"
                aria-label="Edit custom price"
                className={styles.actionButton}
                onClick={() => setRowToEdit(row)}
            >
                <FontAwesomeIcon icon={faPen} />
            </button>
        </Box>
    );

    const emptyState = (
        <Box
            className={styles.emptyState}
            data-testid="pet-custom-prices-empty-state"
        >
            <img
                className={styles.emptyImage}
                src="/images/memberships/clients-notfound.svg"
                alt=""
                width={330}
                height={250}
            />
            <Typography className={styles.emptyTitle}>No custom prices yet</Typography>
            <Typography className={styles.emptyBody}>
                There are no custom prices saved for this pet&apos;s services yet.
            </Typography>
        </Box>
    );

    const desktopTable = (
        <Table
            className={styles.table}
            aria-label="Pet custom prices"
        >
            <TableHead>
                <TableRow className={styles.headerRow}>
                    <TableCell className={styles.headerCell}>Service</TableCell>
                    <TableCell className={styles.headerCell}>Category</TableCell>
                    <TableCell className={styles.headerCell}>Price</TableCell>
                    <TableCell className={styles.headerCell}>Last Updated</TableCell>
                    <TableCell className={styles.headerCell} />
                </TableRow>
            </TableHead>
            <TableBody>
                {pageRows.map(row => (
                    <TableRow
                        key={row.key}
                        className={styles.bodyRow}
                    >
                        <TableCell>{renderServiceCell(row)}</TableCell>
                        <TableCell>
                            <Typography className={styles.cellText}>{row.category || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={styles.cellText}>{formatCustomPrice(row.price)}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={styles.cellText}>{formatLastUpdated(row.updatedAt)}</Typography>
                        </TableCell>
                        <TableCell className={styles.actionsCell}>{renderActions(row)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const mobileList = (
        <Box
            className={styles.mobileList}
            aria-label="Pet custom prices"
        >
            {pageRows.map(row => {
                const pillLabel = getTransportationPillLabel(row.priceType);
                return (
                    <Box
                        key={row.key}
                        className={styles.mobileCard}
                    >
                        <Box className={styles.mobileTop}>
                            <Box className={styles.mobileService}>
                                <Typography className={styles.mobileServiceName}>{row.serviceName}</Typography>
                                {pillLabel && <span className={styles.transportPill}>{pillLabel}</span>}
                                <Typography className={styles.mobileCategory}>{row.category || '—'}</Typography>
                            </Box>
                            <Typography className={styles.mobilePrice}>{formatCustomPrice(row.price)}</Typography>
                        </Box>
                        <Box className={styles.mobileBottom}>
                            <Box>
                                <Typography className={styles.mobileMetaLabel}>Last Updated</Typography>
                                <Typography className={styles.mobileMetaValue}>
                                    {formatLastUpdated(row.updatedAt)}
                                </Typography>
                            </Box>
                            {renderActions(row)}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );

    return (
        <>
            <Box className={styles.container}>
                <Box className={styles.headerWrap}>
                    <DefaultHeader
                        title="Custom Prices"
                        textBelowTitle="View and manage custom service prices saved for this pet."
                    />
                </Box>

                {isLoading ? (
                    <Box
                        className={styles.loadingWrap}
                        role="status"
                        aria-live="polite"
                        aria-busy="true"
                    >
                        <Spinner size={48} />
                    </Box>
                ) : totalCount === 0 ? (
                    emptyState
                ) : (
                    <>
                        {isMobile ? mobileList : desktopTable}
                        <Box className={styles.paginationRow}>
                            <Pagination
                                count={totalCount}
                                page={safePage}
                                pageSize={pageSize}
                                onPageChange={setPage}
                                onPageSizeChange={size => {
                                    setPageSize(size);
                                    setPage(1);
                                }}
                                className={styles.pagination}
                            />
                        </Box>
                    </>
                )}
            </Box>

            {rowToDelete && (
                <ConfirmDialog
                    id="pet_custom_price_delete_"
                    open={true}
                    className={styles.deleteModal}
                    title={<span className={styles.deleteTitle}>Delete Custom Price</span>}
                    question={
                        <span className={styles.deleteQuestion}>
                            Are you sure you want to delete this custom
                            <br />
                            service price?
                        </span>
                    }
                    cancelButtonLabel="No"
                    confirmButtonLabel="Yes"
                    processing={isDeleting || (rowToDelete.hasSiblingOnSameRecord && isUpdating)}
                    onCancel={() => setRowToDelete(null)}
                    onConfirm={handleConfirmDelete}
                />
            )}

            {rowToEdit && (
                <EditCustomPriceModal
                    key={rowToEdit.key}
                    open={true}
                    row={rowToEdit}
                    processing={isUpdating}
                    onCancel={() => setRowToEdit(null)}
                    onSave={handleSaveEdit}
                />
            )}
        </>
    );
};

export default PetCustomPricesTab;
